import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { makeApiRequest, API_ENDPOINTS } from '../config/api';
import './Industries.css';

interface Industry {
  id: string;
  name: string;
  description: string;
  image: string;
  video?: string; // Video URL for modal display
  slug: string;
  order?: number;
  empowerment?: string;
}

interface IntroSection {
  heading: string;
  paragraphs: string[];
  image?: string;
  video?: string;
}

interface IndustriesPageData {
  heading: string;
  subheading: string;
  tagline?: string;
  description: string;
  intro?: IntroSection;
  industries: Industry[];
}

const Industries: React.FC = () => {
  const navigate = useNavigate();
  const [pageData, setPageData] = useState<IndustriesPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIndustry, setSelectedIndustry] = useState<Industry | null>(null);
  const [modalLoading, setModalLoading] = useState(false);

  const handleIndustryClick = (industry: Industry) => {
    setModalLoading(true);

    // Simulate loading delay for better UX
    setTimeout(() => {
      setSelectedIndustry(industry);
      setModalLoading(false);
    }, 300);
  };

  const closeModal = () => {
    setSelectedIndustry(null);
  };

  const handleRequestDemo = () => {
    // Close the modal first
    setSelectedIndustry(null);

    // Navigate to contact page
    navigate('/contact');
  };

  useEffect(() => {
    console.log('Industries useEffect running - should only run once');
    const fetchIndustries = async () => {
      try {
        console.log('Fetching industries data');
        const response = await makeApiRequest(API_ENDPOINTS.INDUSTRIES);

        if (response.ok) {
          const data = await response.json();
          // data now contains: { heading, subheading, tagline, description, industries }
          setPageData(data);
        } else {
          throw new Error('Failed to fetch industries');
        }
      } catch (err) {
        console.error('Error fetching industries:', err);
        setError('Failed to load industries data');
      } finally {
        setLoading(false);
      }
    };

    fetchIndustries();
  }, []);

  if (loading) {
    return (
      <div className="industries-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading Industries...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="industries-page">
        <div className="error-container">
          <h2>Error Loading Industries</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="industries-page">
      {/* Genesis Introduction Section */}
      {pageData?.intro && (
        <section className="industries-intro">
          <div className="container">
            <div className="intro-text-full">
              <h2>{pageData.intro.heading}</h2>
              {pageData.intro.paragraphs.map((paragraph, index) => (
                <p key={index}>{paragraph}</p>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Industries Grid */}
      <section className="industries-content">
        <div className="container">
          <div className="industries-grid">
            {pageData?.industries.map((industry) => (
                <div 
                  key={industry.id} 
                  className="industry-card"
                  onClick={() => handleIndustryClick(industry)}
                >
                  <div className="industry-image">
                    <img 
                      src={industry.image} 
                      alt={industry.name}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                    <div className="image-fallback">
                    <div className="fallback-icon">🏭</div>
                    </div>
                  </div>
                  <div className="industry-content">
                  <h3>{industry.name}</h3>
                  <div className="industry-meta">
                    <span className="learn-more">Learn More →</span>
                  </div>
                </div>
          </div>
            ))}
          </div>
      </div>
      </section>

      {/* Industry Detail Modal */}
      {selectedIndustry && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            {modalLoading ? (
              <div className="modal-loading">
                <div className="loading-spinner"></div>
                <p>Loading industry details...</p>
              </div>
            ) : (
              <>
                <div className="modal-header">
                  <h2>{selectedIndustry.name}</h2>
                  <button className="close-btn" onClick={closeModal}>×</button>
                </div>

                <div className="modal-body">
                  <div className="industry-overview-layout">
                    {/* Left side: Video */}
                    <div className="industry-video-container">
                      {selectedIndustry.video ? (
                        <video
                          className="industry-video"
                          autoPlay
                          muted
                          loop
                          playsInline
                        >
                          <source src={selectedIndustry.video} type="video/mp4" />
                          Your browser does not support the video tag.
                        </video>
                      ) : (
                        <div className="video-placeholder">
                          <div className="placeholder-icon">🎥</div>
                          <p>Video coming soon</p>
                        </div>
                      )}
                    </div>

                    {/* Right side: Empowerment text */}
                    <div className="industry-empowerment">
                      <h3>Industry Overview</h3>
                      <div className="empowerment-content">
                        <p>{selectedIndustry.empowerment}</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="modal-footer">
                  <button className="btn btn-primary" onClick={handleRequestDemo}>
                    Request Demo
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Industries; 